import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  FormatUnderlined as UnderlineIcon,
  FormatListBulleted as BulletListIcon,
  FormatListNumbered as NumberListIcon,
} from '@mui/icons-material';
import {
  clinicalBodyIsEmpty,
  sanitizeClinicalHtml,
  toClinicalEditorHtml,
} from '../../utils/clinicalHtml';

export interface ClinicalHtmlEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minRows?: number;
  label?: string;
  disabled?: boolean;
  /** Troca de evolução/rascunho — reinicia cache interno do contentEditable. */
  contentKey?: string | number | null;
  /** Menos margem no rótulo e padding interno (notebooks). */
  dense?: boolean;
}

const ClinicalHtmlEditor: React.FC<ClinicalHtmlEditorProps> = ({
  value,
  onChange,
  placeholder,
  minRows = 10,
  label,
  disabled = false,
  contentKey = null,
  dense = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isProgrammaticSyncRef = useRef(false);
  // null = ainda não sincronizado; garante que o innerHTML seja populado na
  // montagem mesmo quando `value` já vem preenchido (ex.: modelo pré-selecionado).
  const lastEmittedRef = useRef<string | null>(null);
  const isComposingRef = useRef(false);

  useLayoutEffect(() => {
    lastEmittedRef.current = null;
  }, [contentKey]);

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value === lastEmittedRef.current) return;
    const html = toClinicalEditorHtml(value);
    isProgrammaticSyncRef.current = true;
    if (el.innerHTML !== html) {
      el.innerHTML = html;
    }
    lastEmittedRef.current = value;
    queueMicrotask(() => {
      isProgrammaticSyncRef.current = false;
    });
  }, [value, contentKey]);

  const emitChange = useCallback(() => {
    if (isProgrammaticSyncRef.current) return;
    const el = editorRef.current;
    if (!el) return;
    const sanitized = sanitizeClinicalHtml(el.innerHTML);
    lastEmittedRef.current = sanitized;
    onChange(sanitized);
  }, [onChange]);

  const runCommand = (command: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false);
    emitChange();
  };

  const handleToolbarMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
  };

  const minHeight = Math.max(6, minRows) * 24;

  return (
    <Box>
      {label ? (
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, mb: dense ? 0.35 : 1, fontSize: dense ? '0.8rem' : undefined, color: '#1d4ed8' }}
        >
          {label}
        </Typography>
      ) : null}
      <Box
        sx={{
          border: '1px solid #cbd5e1',
          borderRadius: 1,
          overflow: 'hidden',
          opacity: disabled ? 0.65 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          bgcolor: disabled ? '#f8fafc' : 'transparent',
          '&:focus-within': disabled
            ? {}
            : {
                borderColor: '#0284c7',
                boxShadow: '0 0 0 1px #0284c7',
              },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.25,
            px: 0.5,
            py: 0.5,
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          {[
            { cmd: 'bold', title: 'Negrito', icon: <BoldIcon fontSize="small" /> },
            { cmd: 'italic', title: 'Itálico', icon: <ItalicIcon fontSize="small" /> },
            { cmd: 'underline', title: 'Sublinhado', icon: <UnderlineIcon fontSize="small" /> },
          ].map(({ cmd, title, icon }) => (
            <Tooltip key={cmd} title={title}>
              <IconButton
                size="small"
                onMouseDown={handleToolbarMouseDown}
                onClick={() => runCommand(cmd)}
                sx={{ color: '#475569' }}
              >
                {icon}
              </IconButton>
            </Tooltip>
          ))}
          <Box sx={{ width: '1px', bgcolor: '#e2e8f0', mx: 0.5, my: 0.5 }} />
          {[
            { cmd: 'insertUnorderedList', title: 'Lista com tópicos', icon: <BulletListIcon fontSize="small" /> },
            { cmd: 'insertOrderedList', title: 'Lista numerada', icon: <NumberListIcon fontSize="small" /> },
          ].map(({ cmd, title, icon }) => (
            <Tooltip key={cmd} title={title}>
              <IconButton
                size="small"
                onMouseDown={handleToolbarMouseDown}
                onClick={() => runCommand(cmd)}
                sx={{ color: '#475569' }}
              >
                {icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <Box
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-disabled={disabled || undefined}
          data-placeholder={placeholder}
          onInput={() => {
            if (!isComposingRef.current) emitChange();
          }}
          onCompositionStart={() => {
            isComposingRef.current = true;
          }}
          onCompositionEnd={() => {
            isComposingRef.current = false;
            emitChange();
          }}
          onBlur={emitChange}
          sx={{
            minHeight,
            px: dense ? 1 : 1.5,
            py: dense ? 0.75 : 1.25,
            fontSize: dense ? '0.8125rem' : '0.875rem',
            lineHeight: dense ? 1.55 : 1.7,
            color: '#1f2937',
            outline: 'none',
            overflowY: 'auto',
            '&:empty:before': {
              content: 'attr(data-placeholder)',
              color: '#94a3b8',
              pointerEvents: 'none',
            },
            '& ul, & ol': { pl: 3, my: 0.5 },
            '& li': { my: 0.25 },
          }}
        />
      </Box>
    </Box>
  );
};

export { clinicalBodyIsEmpty };
export default ClinicalHtmlEditor;
